import { createContext, useContext, useEffect, useMemo, useRef, useState, type MouseEvent, type ReactElement, type ReactNode } from 'react';
import type { Locale } from '@gr/core/platform/user-preferences/locales';
import type { PublicConfig } from '@gr/core/platform/config/public-config';
import { RealtimeMessageType, type RealtimeEvent } from '@gr/core/platform/system/realtime';
import { ErrorCode } from '@gr/core/errors';
import { useObservable } from '../shared/store';
import type { Params } from '../shared/i18n/i18n';
import type { NavigateOptions, RouteMatch } from '../shared/router/router';
import type { SessionState } from '../contexts/accounts/identity/session.service';
import type { AppServices } from './services';
import type { RouteKey } from './routes';

const ServicesContext = createContext<AppServices | null>(null);

export function ServicesProvider({ services, children }: { services: AppServices; children: ReactNode }): ReactElement {
  return <ServicesContext.Provider value={services}>{children}</ServicesContext.Provider>;
}

export function useServices(): AppServices {
  const services = useContext(ServicesContext);
  if (!services) throw new Error('ServicesProvider не подключён');
  return services;
}

export interface Translator {
  t(key: string, params?: Params): string;
  tp(key: string, count: number, params?: Params): string;
  /** Текст ошибки по коду ErrorCode (или первому коду списка). */
  te(codes: readonly string[] | string | null | undefined): string;
  locale: Locale;
}

export function useT(): Translator {
  const { i18n } = useServices();
  const locale = useObservable(i18n.locale);
  return useMemo(
    () => ({
      t: i18n.t,
      tp: i18n.tp,
      te: (codes) => {
        const code = (Array.isArray(codes) ? codes[0] : codes) ?? ErrorCode.InternalError;
        return i18n.has(`errors.${code}`) ? i18n.t(`errors.${code}`) : i18n.t('errors.UNKNOWN', { code });
      },
      locale,
    }),
    // locale в зависимостях — пересоздать переводчик при смене языка.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n, locale],
  );
}

/** CFG-UC-1: публичная конфигурация; компонент перерисуется, когда администратор её изменит. */
export function useConfig(): PublicConfig {
  return useObservable(useServices().config.value);
}

export function useSession(): SessionState {
  return useObservable(useServices().session.state);
}

/** SYS-UC-13: сообщения соединения событий; слушатель может меняться от рендера к рендеру. */
export function useRealtimeMessages(listener: (message: RealtimeEvent) => void): void {
  const { realtime } = useServices();
  const latest = useRef(listener);
  latest.current = listener;
  useEffect(() => realtime.onMessage((message) => latest.current(message)), [realtime]);
}

/** PUB-UC-7, SYS-UC-13: число ожидающих публикаций блога — приходит само, без опроса. */
export function useDueCount(blogId: string | null): number {
  const { realtime } = useServices();
  const [counts, setCounts] = useState<Readonly<Record<string, number>>>({});
  useEffect(() => realtime.subscribe(blogId), [realtime, blogId]);
  useRealtimeMessages((message) => {
    if (message.type === RealtimeMessageType.DueCount) setCounts((known) => ({ ...known, [message.blogId]: message.count }));
  });
  return blogId ? (counts[blogId] ?? 0) : 0;
}

/** ADM-UC-3: пока компонент на экране, соединение событий следит за «Онлайн». */
export function useWatchOnline(): void {
  const { realtime } = useServices();
  useEffect(() => {
    realtime.watchOnline(true);
    return () => realtime.watchOnline(false);
  }, [realtime]);
}

/** SYS-UC-13.CC-1: связи нет дольше пары секунд. */
export function useConnectionProblem(): { problem: boolean; reconnect(): void } {
  const { realtime } = useServices();
  return { problem: useObservable(realtime.problem), reconnect: () => realtime.reconnectNow() };
}

export function useRoute(): RouteMatch<RouteKey> | null {
  const { router } = useServices();
  const location = useObservable(router.location);
  return useMemo(() => router.match(location.pathname), [router, location]);
}

export function useSearchParams(): URLSearchParams {
  const { router } = useServices();
  const location = useObservable(router.location);
  return useMemo(() => new URLSearchParams(location.search), [location]);
}

export function useNavigate(): (key: RouteKey, params?: Record<string, string>, options?: NavigateOptions & { search?: string }) => void {
  const { router } = useServices();
  return (key, params = {}, { search, ...options } = {}) => router.navigate(`${router.path(key, params)}${search ?? ''}`, options);
}

export function Link({
  to,
  params,
  search,
  children,
  className,
  ariaCurrent,
  keepScroll,
  onClick,
}: {
  to: RouteKey;
  params?: Record<string, string>;
  search?: string;
  children: ReactNode;
  className?: string;
  ariaCurrent?: boolean;
  /** Переход внутри страницы: прокрутка не сбрасывается. */
  keepScroll?: boolean;
  onClick?(): void;
}): ReactElement {
  const { router } = useServices();
  const href = `${router.path(to, params)}${search ?? ''}`;
  const handle = (event: MouseEvent<HTMLAnchorElement>): void => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    onClick?.();
    router.navigate(href, { keepScroll });
  };
  return (
    <a href={href} className={className} onClick={handle} aria-current={ariaCurrent ? 'page' : undefined}>
      {children}
    </a>
  );
}
